// src/utils/workflowEvents.ts
// Phase 3 cross-module event store — shared in-memory state for workflow wires
// Read by: Dashboard, Jobs, Handover, Toolbox, Incidents, ITP, ArchiveCentre
// Written by: ITP, Incidents, SWMS, RFQPortal, Handover, WorkInstructions

// ── P3-3: Handover readiness flags ───────────────────────────────
// Set by ITP when all hold points are released for a job.
// Read by Jobs page and Handover page to show readiness indicator.

const _handoverReadyJobs = new Set<string>()

export function setHandoverReadyFlag(jobId: string): void {
  _handoverReadyJobs.add(jobId)
}
export function getHandoverReadyFlag(jobId: string): boolean {
  return _handoverReadyJobs.has(jobId)
}
export function getAllHandoverReadyJobs(): string[] {
  return [..._handoverReadyJobs]
}
export function clearHandoverReadyFlag(jobId: string): void {
  _handoverReadyJobs.delete(jobId)
}

// ── P3-6: Critical incident alerts ───────────────────────────────
// Set by Incidents when severity=CRITICAL or treatment=HOSPITAL.
// Read by Dashboard compliance widget and Incidents page.

export interface CriticalAlert {
  id: string
  incidentNumber: string
  jobId: string
  jobNumber: string
  description: string
  severity: 'CRITICAL' | 'HIGH'
  notifiableToWorksafe: boolean
  createdAt: string
  createdByName: string
  acknowledged: boolean
}

const _criticalAlerts: CriticalAlert[] = []

export function addCriticalAlert(
  alert: Omit<CriticalAlert, 'id' | 'acknowledged'>,
): void {
  _criticalAlerts.push({ ...alert, id: 'alert-' + Date.now(), acknowledged: false })
}
export function getCriticalAlerts(): CriticalAlert[] {
  return [..._criticalAlerts]
}
export function getUnacknowledgedAlerts(): CriticalAlert[] {
  return _criticalAlerts.filter(a => !a.acknowledged)
}
export function acknowledgeAlert(id: string): void {
  const a = _criticalAlerts.find(x => x.id === id)
  if (a) a.acknowledged = true
}

// ── P3-5: SWMS → Toolbox Talk auto-generation tracking ───────────
// Set by SWMS when approved. Read by Toolbox to highlight auto-drafts.

const _toolboxGeneratedFor = new Set<string>()  // swmsIds

export function markToolboxGenerated(swmsId: string): void {
  _toolboxGeneratedFor.add(swmsId)
}
export function isToolboxGenerated(swmsId: string): boolean {
  return _toolboxGeneratedFor.has(swmsId)
}

// ── P3-2: RFQ award → Draft PO store ─────────────────────────────
// Set by RFQPortal when a quote is awarded.
// Read by PurchaseOrders to surface the draft PO for review.

export interface DraftPOFromRFQ {
  id: string
  poRef: string
  rfqRef: string
  jobRef: string
  supplier: string
  totalExGST: number
  createdAt: string
  status: 'DRAFT'
}

const _draftPOs: DraftPOFromRFQ[] = []

export function addDraftPO(
  po: Omit<DraftPOFromRFQ, 'id' | 'status'>,
): void {
  // Prevent duplicates (award button clicked twice)
  if (_draftPOs.some(p => p.poRef === po.poRef)) return
  _draftPOs.push({ ...po, id: 'dpo-' + Date.now(), status: 'DRAFT' })
}
export function getDraftPOs(): DraftPOFromRFQ[] {
  return [..._draftPOs]
}
export function getDraftPOCount(): number {
  return _draftPOs.length
}

// ── P3-4: Handover complete → auto-filed archive documents ────────
// Set by Handover when PM generates and files the handover pack.
// Read by ArchiveCentre DocumentsTab to show auto-filed entries.

export interface AutoFiledHandover {
  id: string
  jobId: string
  jobNumber: string
  description: string
  clientName: string
  filedAt: string
  filedByName: string
  packRef: string   // e.g. 'HANDOVER-TP001-20260701'
}

const _autoFiledHandovers: AutoFiledHandover[] = []

export function autoFileHandoverToArchive(record: {
  jobId: string; jobNumber: string; description: string; clientName: string
}, filedByName = 'PM'): AutoFiledHandover {
  const now    = new Date()
  const packRef = 'HANDOVER-' + record.jobNumber.replace(/[^A-Z0-9]/g,'') + '-' + now.toISOString().slice(0,10).replace(/-/g,'')
  const entry: AutoFiledHandover = {
    id:          'afh-' + Date.now(),
    jobId:       record.jobId,
    jobNumber:   record.jobNumber,
    description: record.description,
    clientName:  record.clientName,
    filedAt:     now.toISOString(),
    filedByName,
    packRef,
  }
  // Prevent duplicate filing within same session
  if (!_autoFiledHandovers.some(h => h.jobId === record.jobId)) {
    _autoFiledHandovers.push(entry)
  }
  return entry
}
export function getAutoFiledHandovers(): AutoFiledHandover[] {
  return [..._autoFiledHandovers]
}
export function isHandoverFiled(jobId: string): boolean {
  return _autoFiledHandovers.some(h => h.jobId === jobId)
}

// ── P3-7: Work Instruction completion → ITP flag ─────────────────
// Set by WorkInstructions when all non-hold-point steps are signed off.
// Read by ITP to surface "WI complete — witness points may be ready".

const _wiCompletedJobs = new Set<string>()  // jobIds with all WI steps signed

export function recordWICompletionForJob(jobId: string): void {
  if (jobId) _wiCompletedJobs.add(jobId)
}
export function isWICompletedForJob(jobId: string): boolean {
  return _wiCompletedJobs.has(jobId)
}
export function getAllWICompletedJobs(): string[] {
  return [..._wiCompletedJobs]
}
